import React from 'react';
import { RotateCcw, AlertCircle, CheckCircle, XCircle, Mail } from 'lucide-react';

const ReturnsPage = () => {
  return (
    <div className="min-h-screen pt-24 bg-[#050505]">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00F0FF] mb-2">Policy</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#F8F8F8]">
            Returns Policy
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Overview */}
        <div className="bg-[#111111] border border-[#222222] p-8 mb-12">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-[#111111] border border-[#00F0FF] flex items-center justify-center">
              <RotateCcw className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <h2 className="text-xl font-medium text-[#F8F8F8]">Returns & Refunds</h2>
          </div>
          <p className="text-[#A0A0A0] leading-relaxed">
            At DIY PPF Kit, we want you to be completely satisfied with your purchase. Due to the 
            custom-cut nature of our products, we have specific policies in place to ensure fairness 
            for all customers.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {/* Eligible Returns */}
          <div className="bg-[#111111] border border-[#222222] p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="w-5 h-5 text-[#34C759]" />
              <h3 className="text-lg font-medium text-[#F8F8F8]">Eligible for Return</h3>
            </div>
            <ul className="space-y-3 text-[#A0A0A0] text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-[#34C759]">•</span>
                <span>Unopened kits in original packaging within 14 days of delivery</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#34C759]">•</span>
                <span>Damaged products received due to shipping (report within 48 hours)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#34C759]">•</span>
                <span>Manufacturing defects in the film (covered under warranty)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#34C759]">•</span>
                <span>Incorrect kit sent (wrong vehicle specification)</span>
              </li>
            </ul>
          </div>

          {/* Not Eligible */}
          <div className="bg-[#111111] border border-[#222222] p-6">
            <div className="flex items-center space-x-3 mb-4">
              <XCircle className="w-5 h-5 text-[#FF3B30]" />
              <h3 className="text-lg font-medium text-[#F8F8F8]">Not Eligible for Return</h3>
            </div>
            <ul className="space-y-3 text-[#A0A0A0] text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-[#FF3B30]">•</span>
                <span>Opened or partially used kits</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FF3B30]">•</span>
                <span>Kits with customer-caused damage</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FF3B30]">•</span>
                <span>Installation errors or application issues</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FF3B30]">•</span>
                <span>Products past the 14-day return window</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#FF3B30]">•</span>
                <span>Change of mind after kit has been customised and cut</span>
              </li>
            </ul>
          </div>

          {/* Return Process */}
          <div className="bg-[#111111] border border-[#222222] p-6">
            <h3 className="text-lg font-medium text-[#F8F8F8] mb-4">Return Process</h3>
            <ol className="space-y-4 text-[#A0A0A0] text-sm">
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center flex-shrink-0 text-[#00F0FF] font-bold text-xs">1</span>
                <span>Contact us at Sales@DIYPPFKit.com.au with your order number and reason for return</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center flex-shrink-0 text-[#00F0FF] font-bold text-xs">2</span>
                <span>Receive a Return Authorisation (RA) number within 2 business days</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center flex-shrink-0 text-[#00F0FF] font-bold text-xs">3</span>
                <span>Ship the product back in original packaging with the RA number clearly marked</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center flex-shrink-0 text-[#00F0FF] font-bold text-xs">4</span>
                <span>Once received and inspected, refund will be processed within 5-7 business days</span>
              </li>
            </ol>
          </div>

          {/* Refund Information */}
          <div className="bg-[#111111] border border-[#222222] p-6">
            <h3 className="text-lg font-medium text-[#F8F8F8] mb-4">Refund Information</h3>
            <ul className="space-y-3 text-[#A0A0A0] text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-[#00F0FF]">•</span>
                <span>Refunds are processed to the original payment method</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#00F0FF]">•</span>
                <span>Original shipping costs are non-refundable unless we made an error</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#00F0FF]">•</span>
                <span>Return shipping is the customer's responsibility for change of mind returns</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-[#00F0FF]">•</span>
                <span>A 15% restocking fee may apply for unopened returns</span>
              </li>
            </ul>
          </div>

          {/* Important Notice */}
          <div className="bg-[#1A1A1A] border border-[#FFCC00] p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-[#F8F8F8] font-medium mb-2">Important Notice</h3>
                <p className="text-[#A0A0A0] text-sm leading-relaxed">
                  Due to the custom nature of our PPF kits (each kit is precision-cut for your specific 
                  vehicle make, model, and year), we strongly recommend double-checking your vehicle 
                  details before placing an order. Once a kit has been cut, it cannot be returned for 
                  change of mind.
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-[#111111] border border-[#222222] p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="w-5 h-5 text-[#00F0FF]" />
              <h3 className="text-lg font-medium text-[#F8F8F8]">Questions?</h3>
            </div>
            <p className="text-[#A0A0A0] text-sm">
              If you have any questions about our returns policy, please contact us at{' '}
              <a href="mailto:Sales@DIYPPFKit.com.au" className="text-[#00F0FF] hover:underline">
                Sales@DIYPPFKit.com.au
              </a>
              {' '}or call us on{' '}
              <a href="tel:0411911858" className="text-[#00F0FF] hover:underline">
                0411 911 858
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPage;
